import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { EntryAnnotations, RepositoryFile } from '@trove/core';
import { MarqueeText } from '@trove/ui';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { ancestorPaths, buildFileTree, parentFolder, type FileTreeNode } from '../fileTree';
import { FileGlyph } from './FileGlyph';
import styles from './SidebarFileTree.module.css';

function TreeRows({
  nodes,
  depth,
  expanded,
  activeFolder,
  activeFile,
  annotations,
  onToggle,
  onOpenFolder,
  onOpenFile,
}: {
  nodes: FileTreeNode[];
  depth: number;
  expanded: Set<string>;
  activeFolder?: string;
  activeFile?: string;
  annotations?: EntryAnnotations;
  onToggle(path: string): void;
  onOpenFolder(path: string): void;
  onOpenFile(path: string): void;
}) {
  return (
    <>
      {nodes.map((node) => {
        const isFolder = node.type === 'folder';
        const open = isFolder && expanded.has(node.path);
        const selected =
          (isFolder && !activeFile && activeFolder === node.path) ||
          (!isFolder && activeFile === node.path);
        const paddingLeft = 8 + depth * 12;
        const annotation = annotations?.[node.path];

        return (
          <div key={node.path} className={styles.node}>
            <button
              type="button"
              className={`${styles.row} ${selected ? styles.rowSelected : ''}`}
              style={{ paddingLeft }}
              data-marquee-hover=""
              aria-expanded={isFolder ? open : undefined}
              aria-current={selected ? 'true' : undefined}
              onClick={() => {
                if (isFolder) {
                  if (!open) onToggle(node.path);
                  onOpenFolder(node.path);
                } else {
                  onOpenFile(node.path);
                }
              }}
            >
              {isFolder ? (
                <span
                  className={styles.chevron}
                  aria-hidden="true"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onToggle(node.path);
                  }}
                >
                  {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
                </span>
              ) : (
                <span className={styles.chevronSpacer} />
              )}
              <FileGlyph
                entry={node}
                open={open}
                size="tree"
                iconOverride={annotation?.icon}
                colorOverride={annotation?.color}
              />
              <span className={styles.label}>
                <MarqueeText className={styles.name}>{node.name}</MarqueeText>
                {annotation?.tags?.length ? (
                  <span className={styles.tagHint}>{annotation.tags[0]}</span>
                ) : null}
              </span>
            </button>
            {isFolder && open && node.children.length > 0 && (
              <TreeRows
                nodes={node.children}
                depth={depth + 1}
                expanded={expanded}
                activeFolder={activeFolder}
                activeFile={activeFile}
                annotations={annotations}
                onToggle={onToggle}
                onOpenFolder={onOpenFolder}
                onOpenFile={onOpenFile}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

export function SidebarFileTree({
  files,
  folder,
  file,
  annotations,
}: {
  files: RepositoryFile[];
  folder?: string;
  file?: string;
  annotations?: EntryAnnotations;
}) {
  const navigate = useNavigate();
  const tree = buildFileTree(files);
  const focusPath = file ?? folder;
  const [expanded, setExpanded] = useState(() => new Set(ancestorPaths(focusPath)));

  useEffect(() => {
    const required = ancestorPaths(focusPath);
    if (!required.length) return;
    setExpanded((current) => {
      const next = new Set(current);
      let changed = false;
      for (const path of required) {
        if (!next.has(path)) {
          next.add(path);
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [focusPath]);

  const toggle = (path: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const openFolder = (path: string) => {
    void navigate({ to: '/', search: { folder: path } });
  };

  const openFile = (path: string) => {
    void navigate({
      to: '/',
      search: { folder: parentFolder(path) ?? folder, file: path },
    });
  };

  return (
    <div className={styles.tree} role="tree" aria-label="Repository files">
      {tree.length ? (
        <TreeRows
          nodes={tree}
          depth={0}
          expanded={expanded}
          activeFolder={folder}
          activeFile={file}
          annotations={annotations}
          onToggle={toggle}
          onOpenFolder={openFolder}
          onOpenFile={openFile}
        />
      ) : (
        <div className={styles.empty}>No files yet</div>
      )}
    </div>
  );
}
